import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getObjectId, type Order } from '@hero/schema';
import { useCurrentUserApi, useMessageSendApi } from '@hero/api-hooks';
import { Modal } from '../../../app/Modal';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox/Checkbox';
import {
  buildWaybillRecipientRows,
  type WaybillRecipientRow,
} from './buildWaybillRecipientRows';

export interface OrderSendWaybillModalProps {
  order: Order;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Multi-select Send waybill modal. "Send to Me" is auto-selected when the
 * current user has a phone. Confirm → `useMessageSendApi` (Messaging API).
 */
export const OrderSendWaybillModal: React.FC<OrderSendWaybillModalProps> = ({
  order,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation(['m-order', 'c-main']);
  const { data: meData } = useCurrentUserApi();
  const me = meData?.user;

  const rows = useMemo(
    () =>
      buildWaybillRecipientRows({
        order,
        me: me?.phone ? { name: me.name, phone: me.phone } : undefined,
      }),
    [order, me?.name, me?.phone],
  );

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!isOpen) return;
    setSelectedKeys(new Set(rows.filter(r => r.defaultSelected).map(r => r.key)));
  }, [isOpen, rows]);

  const hasWaybill = !!order.waybill?.image?.url;

  const sendMessage = useMessageSendApi({
    onSuccess: data => {
      const failed = data.failed?.length ?? 0;
      if (failed > 0 && data.sent > 0) {
        toast.success(
          t('m-order:toast.success.waybill_sent_partial', {
            sent: data.sent,
            failed,
          }),
        );
      } else if (data.sent > 0) {
        toast.success(
          t('m-order:toast.success.waybill_sent', { count: data.sent }),
        );
      } else {
        toast.error(
          t('m-order:toast.error.waybill_send_failed', {
            message: data.failed?.[0]?.reason || 'unknown',
          }),
        );
      }
      onClose();
    },
    onError: (error: Error) => {
      toast.error(
        t('m-order:toast.error.waybill_send_failed', { message: error.message }),
      );
    },
  });

  const toggleRow = (row: WaybillRecipientRow) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(row.key)) next.delete(row.key);
      else next.add(row.key);
      return next;
    });
  };

  const selectedPhones = rows.filter(r => selectedKeys.has(r.key)).map(r => r.phone);
  const canConfirm = hasWaybill && selectedPhones.length > 0 && !sendMessage.isPending;

  const handleConfirm = () => {
    if (!canConfirm) return;
    sendMessage.mutate({
      orderId: getObjectId(order._id),
      channel: 'whatsapp',
      templateId: 'order_waybill',
      to: { phones: selectedPhones },
    });
  };

  const handleClose = () => {
    if (sendMessage.isPending) return;
    onClose();
  };

  return (
    <Modal
      btnTitle=""
      isOpen={isOpen}
      onClose={handleClose}
      modalTitle={t('m-order:modal.send_waybill.title')}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('m-order:modal.send_waybill.description')}
        </p>

        {!hasWaybill && (
          <p className="text-sm text-destructive">{t('m-order:modal.send_waybill.no_waybill')}</p>
        )}

        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('m-order:modal.send_waybill.no_recipients')}
          </p>
        ) : (
          <ul className="space-y-2 max-h-[50vh] overflow-y-auto">
            {rows.map(row => {
              const checked = selectedKeys.has(row.key);
              return (
                <li key={row.key}>
                  <label className="flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer hover:bg-muted/40">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleRow(row)}
                      disabled={!hasWaybill || sendMessage.isPending}
                      className="mt-0.5"
                    />
                    <span className="flex flex-col min-w-0 text-start">
                      <span className="font-medium text-sm truncate">{row.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {t(`m-order:modal.send_waybill.roles.${row.role}`)} · {row.phone}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}

        {selectedPhones.length === 0 && rows.length > 0 && hasWaybill && (
          <p className="text-sm text-destructive">
            {t('m-order:modal.send_waybill.empty_selection')}
          </p>
        )}

        <div className="flex gap-2 pt-2 justify-end">
          <Button type="button" variant="outline" onClick={handleClose} disabled={sendMessage.isPending}>
            {t('c-main:crud_buttons.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!canConfirm}>
            {sendMessage.isPending
              ? t('m-order:modal.send_waybill.sending')
              : t('m-order:modal.send_waybill.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
